import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../tjenester/apiClient';

const SWIPE_THRESHOLD = 40;

function PropertyGallery({ images, name, status }) {
  const [activeImage, setActiveImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const touchStartX = useRef(null);

  const hasImages = images.length > 0;
  const hasMultiple = images.length > 1;

  useEffect(() => {
    setActiveImage(0);
  }, [images]);

  const showPrevious = useCallback(() => {
    setActiveImage((index) => (index <= 0 ? images.length - 1 : index - 1));
  }, [images.length]);

  const showNext = useCallback(() => {
    setActiveImage((index) => (index >= images.length - 1 ? 0 : index + 1));
  }, [images.length]);

  const openLightbox = () => {
    if (hasImages) {
      setIsLightboxOpen(true);
    }
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null || !hasMultiple) {
      return;
    }
    const deltaX = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      if (deltaX < 0) {
        showNext();
      } else {
        showPrevious();
      }
    }
    touchStartX.current = null;
  };

  useEffect(() => {
    if (!isLightboxOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeLightbox();
      } else if (event.key === 'ArrowLeft') {
        showPrevious();
      } else if (event.key === 'ArrowRight') {
        showNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isLightboxOpen, showPrevious, showNext]);

  const currentImage = images[activeImage] || images[0];

  return (
    <>
      <div
        className="group relative flex min-h-[340px] items-center justify-center bg-[linear-gradient(145deg,rgba(202,188,157,0.65),rgba(111,141,123,0.45)),repeating-linear-gradient(-45deg,rgba(255,255,255,0.2),rgba(255,255,255,0.2)_12px,transparent_12px,transparent_24px)] text-lg font-semibold text-[#163127]"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {currentImage ? (
          <button
            aria-label="Apne bilde i full storrelse"
            className="absolute inset-0 h-full w-full cursor-zoom-in"
            onClick={openLightbox}
            type="button"
          >
            <img
              alt={name}
              className="h-full w-full object-cover"
              src={apiClient.resolveMediaUrl(currentImage)}
            />
          </button>
        ) : null}

        <span className="pointer-events-none absolute left-6 top-6 z-10 rounded-full bg-[rgba(249,245,238,0.92)] px-3 py-1 text-xs uppercase tracking-[0.12em] text-[#163127]">
          {status}
        </span>

        {currentImage ? null : <span>Bilde for {name}</span>}

        {hasMultiple ? (
          <>
            <button
              aria-label="Forrige bilde"
              className="absolute left-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#1832281f] bg-[rgba(249,245,238,0.92)] text-xl text-[#163127] shadow-[0_12px_28px_rgba(24,50,40,0.18)] transition hover:bg-[#f9f5ee]"
              onClick={showPrevious}
              type="button"
            >
              &lt;
            </button>
            <button
              aria-label="Neste bilde"
              className="absolute right-4 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-[#1832281f] bg-[rgba(249,245,238,0.92)] text-xl text-[#163127] shadow-[0_12px_28px_rgba(24,50,40,0.18)] transition hover:bg-[#f9f5ee]"
              onClick={showNext}
              type="button"
            >
              &gt;
            </button>
            <span className="pointer-events-none absolute bottom-4 right-4 z-10 rounded-full bg-[rgba(22,49,39,0.78)] px-3 py-1 text-xs font-semibold text-[#f9f5ee]">
              {activeImage + 1} / {images.length}
            </span>
          </>
        ) : null}
      </div>

      {hasMultiple ? (
        <div className="flex gap-3 overflow-x-auto px-6 pt-5 md:px-8">
          {images.map((image, index) => (
            <button
              aria-label={`Vis bilde ${index + 1}`}
              className={`h-16 w-24 flex-shrink-0 overflow-hidden rounded-xl border-2 transition ${
                index === activeImage ? 'border-[#1d4c3b]' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
              key={image}
              onClick={() => setActiveImage(index)}
              type="button"
            >
              <img
                alt={`${name} miniatyr ${index + 1}`}
                className="h-full w-full object-cover"
                src={apiClient.resolveMediaUrl(image)}
              />
            </button>
          ))}
        </div>
      ) : null}

      {isLightboxOpen && currentImage ? (
        <div
          aria-label={`Bildevisning for ${name}`}
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(12,24,19,0.92)] p-4"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          role="dialog"
        >
          <button
            aria-label="Lukk bildevisning"
            className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(249,245,238,0.4)] bg-[rgba(255,255,255,0.12)] text-2xl text-[#f9f5ee] transition hover:bg-[rgba(255,255,255,0.22)]"
            onClick={closeLightbox}
            type="button"
          >
            ✕
          </button>

          {hasMultiple ? (
            <button
              aria-label="Forrige bilde"
              className="absolute left-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[rgba(249,245,238,0.4)] bg-[rgba(255,255,255,0.12)] text-2xl text-[#f9f5ee] transition hover:bg-[rgba(255,255,255,0.22)] md:left-8"
              onClick={(event) => {
                event.stopPropagation();
                showPrevious();
              }}
              type="button"
            >
              &lt;
            </button>
          ) : null}

          <img
            alt={`${name} bilde ${activeImage + 1}`}
            className="max-h-[88vh] max-w-[92vw] rounded-2xl object-contain shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
            onClick={(event) => event.stopPropagation()}
            src={apiClient.resolveMediaUrl(currentImage)}
          />

          {hasMultiple ? (
            <>
              <button
                aria-label="Neste bilde"
                className="absolute right-4 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[rgba(249,245,238,0.4)] bg-[rgba(255,255,255,0.12)] text-2xl text-[#f9f5ee] transition hover:bg-[rgba(255,255,255,0.22)] md:right-8"
                onClick={(event) => {
                  event.stopPropagation();
                  showNext();
                }}
                type="button"
              >
                &gt;
              </button>
              <span className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-[rgba(255,255,255,0.14)] px-4 py-1.5 text-sm font-semibold text-[#f9f5ee]">
                {activeImage + 1} / {images.length}
              </span>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

export default PropertyGallery;
